import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  type BadgeColor,
  type BadgeSeverity,
  buildEnvironmentSafetyBadges,
  getBadgeSummaryLabel,
  summarizeBadgeColor,
} from "./environmentSafetyBadgeUtils";

import type { DbSafetyInfo } from "@/services/health/DbSafetyService";
import {
  fetchEnvironmentSafety,
  type StripeSafetyInfo,
} from "@/services/health/EnvironmentSafetyService";
import { getBackendOrigin } from "@/utils/network/getBackendOrigin";
import {
  getStoredPreviewEmailOverride,
  isPreviewEmailOverrideEnvironment,
  PREVIEW_EMAIL_OVERRIDE_UPDATED_EVENT,
  requestPreviewEmailOverrideDialog,
  resolveAdminDeployEnvironment,
  shouldShowEnvironmentSafetyBadges,
} from "@/utils/previewEmailOverride";
import { VNextButton, VNextPill } from "@/vnext/primitives";
import "@/vnext/theme/vnext.css";

const COLLAPSE_STORAGE_KEY = "HC_ADMIN_ENVIRONMENT_BADGES_COLLAPSED_V2";
const RESERVED_WIDTH_CSS_VAR = "--vnext-safety-badges-reserved-width";
const RESERVED_HEIGHT_CSS_VAR = "--vnext-safety-badges-reserved-height";
const BOTTOM_CSS_VAR = "--vnext-safety-badges-bottom";
const COLLAPSED_RESERVED_WIDTH = "208px";
const EXPANDED_RESERVED_WIDTH = "432px";
const COLLAPSED_RESERVED_HEIGHT = "32px";
const EXPANDED_RESERVED_HEIGHT = "168px";

const resolveBadgeTone = (
  color: BadgeColor | BadgeSeverity,
): "default" | "accent" | "success" | "warning" | "danger" => {
  switch (color) {
    case "error":
      return "danger";
    case "warning":
      return "warning";
    case "success":
      return "success";
    case "info":
      return "accent";
    case "default":
    default:
      return "default";
  }
};

const readCollapsePreference = (): boolean | null => {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (raw === "true") return true;
    if (raw === "false") return false;
    return null;
  } catch {
    return null;
  }
};

const writeCollapsePreference = (collapsed: boolean): void => {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? "true" : "false");
  } catch {
    // Best-effort only.
  }
};

export const EnvironmentSafetyBadges = () => {
  const badgesRef = useRef<HTMLElement>(null);
  const showBadges = shouldShowEnvironmentSafetyBadges({
    env: import.meta.env,
  });
  const supportsPreviewEmailOverride = isPreviewEmailOverrideEnvironment();
  const deployEnvironment = resolveAdminDeployEnvironment({
    env: import.meta.env,
  });
  const [dbSafety, setDbSafety] = useState<DbSafetyInfo | null>(null);
  const [stripeSafety, setStripeSafety] = useState<StripeSafetyInfo | null>(
    null,
  );
  const [overrideEmail, setOverrideEmail] = useState<string | null>(() =>
    getStoredPreviewEmailOverride(),
  );
  const [collapsed, setCollapsed] = useState<boolean>(
    () => readCollapsePreference() ?? true,
  );

  useEffect(() => {
    writeCollapsePreference(collapsed);
  }, [collapsed]);

  useEffect(() => {
    if (!showBadges) {
      return;
    }

    let cancelled = false;
    void fetchEnvironmentSafety().then((result) => {
      if (cancelled) {
        return;
      }
      setDbSafety(
        result?.dbSafety ?? {
          isProduction: true,
          dbHost: null,
          dbName: null,
        },
      );
      setStripeSafety(
        result?.stripeSafety ?? {
          runtimeMode: "unknown",
          configuredMode: "unknown",
          modeMatchesConfiguration: null,
        },
      );
    });

    return () => {
      cancelled = true;
    };
  }, [showBadges]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const refreshOverrideEmail = () => {
      setOverrideEmail(getStoredPreviewEmailOverride());
    };

    refreshOverrideEmail();

    window.addEventListener(
      PREVIEW_EMAIL_OVERRIDE_UPDATED_EVENT,
      refreshOverrideEmail,
    );
    window.addEventListener("storage", refreshOverrideEmail);

    return () => {
      window.removeEventListener(
        PREVIEW_EMAIL_OVERRIDE_UPDATED_EVENT,
        refreshOverrideEmail,
      );
      window.removeEventListener("storage", refreshOverrideEmail);
    };
  }, []);

  const badges = useMemo(
    () =>
      buildEnvironmentSafetyBadges({
        deployEnvironment,
        dbSafety,
        stripeSafety,
        overrideEmail,
        apiOrigin: getBackendOrigin(),
      }),
    [dbSafety, deployEnvironment, overrideEmail, stripeSafety],
  );

  // F-002/F-209: Live Alerts has no topbar, so a height-only reservation
  // cannot clear these fixed badges. Publish their actual viewport bottom,
  // including expanded/wrapped content, without changing badge visibility.
  useLayoutEffect(() => {
    const node = badgesRef.current;
    const root = document.documentElement;
    if (!showBadges || badges.length === 0 || !node) {
      root.style.removeProperty(BOTTOM_CSS_VAR);
      return;
    }
    const updateBottom = () => {
      root.style.setProperty(
        BOTTOM_CSS_VAR,
        // Narrow layouts already place badges in normal flow above the app.
        getComputedStyle(node).position === "fixed"
          ? `${Math.ceil(node.getBoundingClientRect().bottom)}px`
          : "0px",
      );
    };
    updateBottom();
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateBottom);
    observer?.observe(node);
    window.addEventListener("resize", updateBottom);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateBottom);
      root.style.removeProperty(BOTTOM_CSS_VAR);
    };
  }, [showBadges, badges.length, collapsed]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    if (!showBadges || badges.length === 0) {
      document.documentElement.style.removeProperty(RESERVED_WIDTH_CSS_VAR);
      document.documentElement.style.removeProperty(RESERVED_HEIGHT_CSS_VAR);
      return;
    }

    document.documentElement.style.setProperty(
      RESERVED_WIDTH_CSS_VAR,
      collapsed ? COLLAPSED_RESERVED_WIDTH : EXPANDED_RESERVED_WIDTH,
    );
    document.documentElement.style.setProperty(
      RESERVED_HEIGHT_CSS_VAR,
      collapsed ? COLLAPSED_RESERVED_HEIGHT : EXPANDED_RESERVED_HEIGHT,
    );

    return () => {
      document.documentElement.style.removeProperty(RESERVED_WIDTH_CSS_VAR);
      document.documentElement.style.removeProperty(RESERVED_HEIGHT_CSS_VAR);
    };
  }, [badges.length, collapsed, showBadges]);

  if (!showBadges || badges.length === 0) {
    return null;
  }

  const summaryColor = summarizeBadgeColor(badges);

  return (
    <aside className="vnext-safety-badges" ref={badgesRef}>
      <div className="vnext-safety-badges__summary">
        <VNextPill tone={resolveBadgeTone(summaryColor)}>
          {getBadgeSummaryLabel(collapsed, badges.length)}
        </VNextPill>
        <button
          aria-label="Toggle environment safety badges"
          className="vnext-sidebar__icon-button vnext-safety-badges__toggle"
          onClick={() => setCollapsed((current) => !current)}
          type="button"
        >
          {collapsed ? "▾" : "▴"}
        </button>
      </div>

      {!collapsed ? (
        <div className="vnext-safety-badges__stack">
          {badges.map((badge) => (
            <div className="vnext-safety-badges__item" key={badge.id}>
              <VNextPill tone={resolveBadgeTone(badge.severity)}>
                {badge.text}
              </VNextPill>
            </div>
          ))}
          {supportsPreviewEmailOverride ? (
            <div className="vnext-safety-badges__actions">
              <VNextButton
                aria-label="Set preview email override"
                onClick={() => requestPreviewEmailOverrideDialog()}
                tone="quiet"
              >
                Change email override
              </VNextButton>
            </div>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
};

export default EnvironmentSafetyBadges;
